import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { YANDEX_MAPS_API_KEY } from '../../../../constants/mapConstants';

let yandexScriptsPromise: Promise<void> | null = null;

// Leaflet.Yandex plugin'inde bilinen bir kaldırma hatası: harita tamamen kaldırılırken
// (React unmount → MapContainer'ın map.remove() çağrısı), plugin'in kendi 'unload'
// dinleyicisi (_destroy) this._container'ı siliyor, ardından katmanın kendi 'remove'
// dinleyicisi this._container.remove() çağırınca "Cannot read properties of undefined
// (reading 'remove')" ile patlıyor. _destroy'un container'ı silmesini engelliyoruz —
// DOM elemanı zaten kaldırılmış olsa da .remove() çağrısı güvenli/idempotent.
function patchYandexLayerRemovalBug() {
  const proto = (window as any).L?.Yandex?.prototype;
  if (!proto || proto.__removalBugPatched) return;
  proto.__removalBugPatched = true;

  proto._destroy = function (e: any) {
    if (!this._map || this._map === e.target) {
      if (this._yandex) {
        try {
          this._yandex.destroy();
        } catch {
          // yok sayılır
        }
        delete this._yandex;
      }
    }
  };
}

function loadYandexScripts(): Promise<void> {
  const w = window as any;
  if (w.L?.Yandex) return Promise.resolve();
  if (yandexScriptsPromise) return yandexScriptsPromise;

  yandexScriptsPromise = new Promise((resolve, reject) => {
    w.L = L;

    const ymapsScript = document.createElement('script');
    ymapsScript.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_MAPS_API_KEY}&lang=tr_TR`;
    ymapsScript.onload = () => {
      w.ymaps.ready(() => {
        const pluginScript = document.createElement('script');
        pluginScript.src = 'https://cdn.jsdelivr.net/gh/shramov/leaflet-plugins/layer/tile/Yandex.js';
        pluginScript.onload = () => {
          patchYandexLayerRemovalBug();
          resolve();
        };
        pluginScript.onerror = reject;
        document.head.appendChild(pluginScript);
      });
    };
    ymapsScript.onerror = reject;
    document.head.appendChild(ymapsScript);
  });

  return yandexScriptsPromise;
}

// CartoDB'nin ücretsiz raster tile katmanı API key istemeye başladığı için taban katman
// Yandex Maps'e taşındı. react-leaflet'in <TileLayer> bileşeni Yandex'i desteklemediğinden
// bu bileşen, Leaflet.Yandex plugin'ini script olarak yükleyip haritaya elle ekliyor.
export default function YandexTileLayer() {
  const map = useMap();
  const layerRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;

    loadYandexScripts().then(() => {
      if (cancelled) return;
      const layer = new (window as any).L.Yandex();
      layer.addTo(map);
      layerRef.current = layer;
    });

    return () => {
      cancelled = true;
      const layer = layerRef.current;
      layerRef.current = null;
      if (!layer) return;
      try {
        map.removeLayer(layer);
      } catch {
        // MapContainer kaldırılırken Leaflet.Yandex katmanı zaten temizlenmiş olabilir
        // (react-leaflet'in kendi map.remove() çağrısıyla yarışıyor) — güvenle yok sayılır.
      }
    };
  }, [map]);

  return null;
}
